import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { 
  generateId, 
  addTimestamps, 
  validateAsset,
  ASSET_SCHEMA 
} from '../../../schemas/dataSchemas';

const AddAssetModal = ({ isOpen, onClose, onAddAsset, editingAsset = null }) => {
  const [formData, setFormData] = useState({
    name: editingAsset?.name || '',
    type: editingAsset?.type || 'Cash',
    currentValue: editingAsset?.currentValue || '',
    purchaseValue: editingAsset?.purchaseValue || '',
    acquisitionDate: editingAsset?.acquisitionDate || new Date().toISOString().split('T')[0],
    expectedReturn: editingAsset?.expectedReturn || '12.5',
    probability: editingAsset?.probability || 80,
    liquidity: editingAsset?.liquidity || 'Liquid',
    description: editingAsset?.description || '',
    saleYear: editingAsset?.saleYear || '',
    riskLevel: editingAsset?.riskLevel || 'Medium',
    isActive: editingAsset?.isActive !== undefined ? editingAsset.isActive : true
  });

  const [errors, setErrors] = useState({});

  // Update form data when editingAsset changes
  useEffect(() => {
    if (editingAsset) {
      setFormData({
        name: editingAsset.name || '',
        type: editingAsset.type || 'Cash',
        currentValue: editingAsset.currentValue || '',
        purchaseValue: editingAsset.purchaseValue || '',
        acquisitionDate: editingAsset.acquisitionDate || new Date().toISOString().split('T')[0],
        expectedReturn: editingAsset.expectedReturn || '12.5',
        probability: editingAsset.probability || 80,
        liquidity: editingAsset.liquidity || 'Liquid',
        description: editingAsset.description || '',
        saleYear: editingAsset.saleYear || '',
        riskLevel: editingAsset.riskLevel || 'Medium',
        isActive: editingAsset.isActive !== undefined ? editingAsset.isActive : true
      });
    } else {
      // Reset form when not editing
      setFormData({
        name: '',
        type: 'Cash',
        currentValue: '',
        purchaseValue: '',
        acquisitionDate: new Date().toISOString().split('T')[0],
        expectedReturn: '12.5',
        probability: 80,
        liquidity: 'Liquid',
        description: '',
        saleYear: '',
        riskLevel: 'Medium',
        isActive: true
      });
    }
    setErrors({});
  }, [editingAsset]);

  const liquidAssetTypes = [
    'Equity',
    'Mutual Funds', 
    'PPF',
    'Fixed Deposits',
    'Cash',
    'Gold',
    'EPF',
    'NPS',
    'Other'
  ];
  const illiquidAssetTypes = [
    'Real Estate',
    'Bonds',
    'Insurance',
    'Other'
  ];
  const availableTypes = formData.liquidity === 'Illiquid' ? illiquidAssetTypes : liquidAssetTypes;

  const liquidityOptions = ['Liquid', 'Illiquid'];
  const riskLevels = ['Low', 'Medium', 'High'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      // If liquidity changes, ensure type is compatible with the new liquidity bucket
      if (name === 'liquidity') {
        const nextAvailable = value === 'Illiquid' ? illiquidAssetTypes : liquidAssetTypes;
        const nextType = nextAvailable.includes(prev.type) ? prev.type : (nextAvailable[0] || '');
        return {
          ...prev,
          liquidity: value,
          type: nextType
        };
      }
      return {
        ...prev,
        [name]: value
      };
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Check required fields first with user-friendly messages
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Asset name is required';
    }
    
    if (!formData.type || formData.type === '') {
      newErrors.type = 'Asset type is required';
    }
    
    if (!formData.currentValue || formData.currentValue === '' || parseFloat(formData.currentValue) <= 0) {
      newErrors.currentValue = 'Current value is required and must be greater than 0';
    }
    
    // Validate amount is a valid number
    const currentValue = parseFloat(formData.currentValue);
    if (formData.currentValue && (isNaN(currentValue) || currentValue <= 0)) {
      newErrors.currentValue = 'Current value must be a valid positive number';
    }
    
    if (!formData.liquidity || formData.liquidity === '') {
      newErrors.liquidity = 'Liquidity is required';
    }
    
    if (!formData.acquisitionDate || formData.acquisitionDate === '') {
      newErrors.acquisitionDate = 'Acquisition date is required';
    }
    
    // Validate expected return
    if (!formData.expectedReturn || formData.expectedReturn === '') {
      newErrors.expectedReturn = 'Expected annual return is required';
    } else {
      const expectedReturn = parseFloat(formData.expectedReturn);
      if (isNaN(expectedReturn) || expectedReturn < 0 || expectedReturn > 50) {
        newErrors.expectedReturn = 'Expected return must be between 0% and 50%';
      }
    }
    
    // Validate probability
    if (formData.probability !== undefined && formData.probability !== '') {
      const probability = parseFloat(formData.probability);
      if (isNaN(probability) || probability < 0 || probability > 100) {
        newErrors.probability = 'Confidence level must be between 0% and 100%';
      }
    }
    
    if (!formData.riskLevel || formData.riskLevel === '') {
      newErrors.riskLevel = 'Risk level is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    // Final validation using schema validator
    const validationData = {
      id: editingAsset?.id || generateId(),
      name: formData.name.trim(),
      type: formData.type,
      currentValue: parseFloat(formData.currentValue),
      purchaseValue: formData.purchaseValue ? parseFloat(formData.purchaseValue) : undefined,
      expectedReturn: parseFloat(formData.expectedReturn),
      probability: parseFloat(formData.probability) || 0,
      riskLevel: formData.riskLevel,
      liquidity: formData.liquidity,
      acquisitionDate: formData.acquisitionDate,
      saleYear: formData.saleYear ? parseInt(formData.saleYear) : undefined,
      description: formData.description,
      isActive: formData.isActive
    };

    console.log('Validation data:', validationData);
    console.log('Expected return value:', validationData.expectedReturn, 'Type:', typeof validationData.expectedReturn);

    const validation = validateAsset(validationData);
    
    if (!validation.isValid) {
      // Merge any additional validation errors
      validation.errors.forEach(error => {
        if (error.includes('name') && !newErrors.name) newErrors.name = error;
        if (error.includes('type') && !newErrors.type) newErrors.type = error;
        if ((error.includes('currentValue') || error.includes('value')) && !newErrors.currentValue) newErrors.currentValue = error;
        if (error.includes('expectedReturn') && !newErrors.expectedReturn) newErrors.expectedReturn = error;
        if (error.includes('probability') && !newErrors.probability) newErrors.probability = error;
        if (error.includes('riskLevel') && !newErrors.riskLevel) newErrors.riskLevel = error;
        if (error.includes('liquidity') && !newErrors.liquidity) newErrors.liquidity = error;
        if (error.includes('acquisitionDate') && !newErrors.acquisitionDate) newErrors.acquisitionDate = error;
      });
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const assetData = {
      id: editingAsset?.id || generateId(),
      name: formData.name.trim(),
      type: formData.type,
      currentValue: parseFloat(formData.currentValue),
      purchaseValue: formData.purchaseValue ? parseFloat(formData.purchaseValue) : parseFloat(formData.currentValue),
      expectedReturn: formData.expectedReturn ? parseFloat(formData.expectedReturn) : 0,
      probability: parseFloat(formData.probability),
      riskLevel: formData.riskLevel,
      liquidity: formData.liquidity,
      acquisitionDate: formData.acquisitionDate,
      saleYear: formData.saleYear ? parseInt(formData.saleYear) : null,
      description: formData.description.trim(),
      isActive: formData.isActive
    };

    console.log('Asset data being submitted:', assetData);
    console.log('Expected return in asset data:', assetData.expectedReturn, 'Type:', typeof assetData.expectedReturn);
    
    // Add timestamps using schema utility
    const finalData = addTimestamps(assetData, !editingAsset);
    
    onAddAsset(finalData);
    onClose();
    
    // Reset form
    setFormData({
      name: '',
      type: 'Cash',
      currentValue: '',
      purchaseValue: '',
      acquisitionDate: new Date().toISOString().split('T')[0],
      expectedReturn: '12.5',
      probability: 80,
      liquidity: 'Liquid',
      description: '',
      saleYear: '',
      riskLevel: 'Medium',
      isActive: true
    });
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Plus" size={16} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {editingAsset ? 'Edit Asset' : 'Add New Asset'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {editingAsset ? 'Update asset details' : 'Add a new asset to your portfolio'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Validation Error Summary */}
          {Object.keys(errors).length > 0 && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-destructive mb-1">
                    Please fix the following errors:
                  </h3>
                  <ul className="text-sm text-destructive space-y-1">
                    {Object.entries(errors).map(([field, error]) => (
                      <li key={field} className="flex items-start gap-2">
                        <span className="text-destructive">•</span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Input
                label="Asset Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Apple Inc. Stock"
                error={errors.name}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${errors.liquidity ? 'text-destructive' : 'text-foreground'}`}>
                Liquidity <span className="text-destructive">*</span>
              </label>
              <select
                name="liquidity"
                value={formData.liquidity}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 ${
                  errors.liquidity 
                    ? 'border-destructive focus:ring-destructive' 
                    : 'border-border focus:ring-primary'
                }`}
              >
                {liquidityOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {errors.liquidity && (
                <p className="text-sm text-destructive mt-1">{errors.liquidity}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${errors.type ? 'text-destructive' : 'text-foreground'}`}>
                Asset Type <span className="text-destructive">*</span>
              </label>
              <select
                name="type"
                value={availableTypes.includes(formData.type) ? formData.type : (availableTypes[0] || '')}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 ${
                  errors.type 
                    ? 'border-destructive focus:ring-destructive' 
                    : 'border-border focus:ring-primary'
                }`}
              >
                {availableTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {errors.type && (
                <p className="text-sm text-destructive mt-1">{errors.type}</p>
              )}
            </div>
          </div>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Current Value"
              name="currentValue"
              type="number"
              value={formData.currentValue}
              onChange={handleInputChange}
              placeholder="50000"
              error={errors.currentValue}
              required
            />

            <Input
              label="Purchase Value (Optional)"
              name="purchaseValue"
              type="number"
              value={formData.purchaseValue}
              onChange={handleInputChange}
              placeholder="45000"
              description="Leave empty to use current value"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Acquisition Date"
              name="acquisitionDate"
              type="date"
              value={formData.acquisitionDate}
              onChange={handleInputChange}
              error={errors.acquisitionDate}
              required
            />

            <Input
              label="Expected Annual Return (%)"
              name="expectedReturn"
              type="number"
              step="0.1"
              value={formData.expectedReturn}
              onChange={handleInputChange}
              placeholder="12.5"
              error={errors.expectedReturn}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Confidence Level (%)"
              name="probability"
              type="number"
              min="0"
              max="100"
              value={formData.probability}
              onChange={handleInputChange}
              placeholder="80"
              error={errors.probability}
              description="How confident are you in the expected return?"
              required
            />

            <div>
              <label className={`block text-sm font-medium mb-2 ${errors.riskLevel ? 'text-destructive' : 'text-foreground'}`}>
                Risk Level <span className="text-destructive">*</span>
              </label>
              <select
                name="riskLevel"
                value={formData.riskLevel}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 ${
                  errors.riskLevel 
                    ? 'border-destructive focus:ring-destructive' 
                    : 'border-border focus:ring-primary'
                }`}
              >
                {riskLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
              {errors.riskLevel && (
                <p className="text-sm text-destructive mt-1">{errors.riskLevel}</p>
              )}
            </div>
          </div>

          {formData.type === 'Real Estate' && (
            <Input
              label="Planned Sale Year (Optional)"
              name="saleYear"
              type="number"
              min="2024"
              max="2050"
              value={formData.saleYear}
              onChange={handleInputChange}
              placeholder="2035"
              description="Year when you plan to sell this property"
            />
          )}

          <Input
            label="Description (Optional)"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Additional details about this asset"
            description="Any additional information about this asset"
          />

          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
            >
              {editingAsset ? 'Update Asset' : 'Add Asset'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAssetModal;